// The items-list inputs of a matrix-family question: one row per sub-question, averaged.
//
// Ported from Matrix.ItemsListView#calcItemsListProps, which is four lists rather than one -- what
// the second column measures is what the question type, and the view it is being read in, decide.
// The shared coffee list view still serves heatmap, menu-test and the correlation finder, so only
// the branches a matrix question reaches are here.

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { getModelValue } from '@aytm/app/stats/lib/stat-page'
import { processText } from '@aytm/app/stats/page/components/lib/utils'
import { getChartTopsMapping } from '@aytm/app/stats/page/question-views/mx/lib/utils'

// the palette repeats every 21 rows
const COLOR_INDEXES = 21

const colorIndexOf = position => ((position - 1) % COLOR_INDEXES) + 1

// a ranking question numbers its rows as answers; every other matrix flavour as sub-questions
const ANSWER_NUMBERED_TYPES = ['ro', 'aro']

const TITLE = 'Answer (average)'

const byPosition = (aRow, bRow, dir) => dir * (aRow.position - bRow.position)

// Rows are compared on one cell, with the position breaking the ties so the order stays stable.
const byCellValue = (index, attr) => (aRow, bRow, dir) =>
  dir * (aRow.cells[index][attr] + aRow.position / 10000 - (bRow.cells[index][attr] + bRow.position / 10000))

const batchAnswerOf = (answer, position) => answer?.[`b${position}`] || {}

// the mark a value cell carries while the answer it reads is being fetched again
const loadingMark = answer => ('loading' === answer ? { loading: true } : {})

const basedOnTitle = answer => `<small>(based on ${formatAmounts(answer, 'total', 0)} responses)</small>`

const titleCell = ({ batch, type }) => ({
  title: processText(batch.text),
  label: `${ANSWER_NUMBERED_TYPES.includes(type) ? 'A' : 'SQ'}${batch.position}`,
  colorIndex: colorIndexOf(batch.position)
})

// A question whose sub-questions were not all answered by everyone reports each one's own base,
// which is a column of its own.
const calcSqExist = (answer, batches) =>
  0 < (answer?.total || 0) &&
  batches.some(batch => 0 < answer.total - (batchAnswerOf(answer, batch.position).total || 0))

const SQ_COLUMN = {
  type: 'value',
  title: 'SQ n',
  sort: byCellValue(1, 'value'),
  format: value => formatAmounts(value)
}

// The column the stored sort names, marked with the direction it is sorted in. An order naming a
// column the list does not offer falls to the last one, the way the coffee list resolved it.
const applySortedColumn = (cols, sortOrder) => {
  const parts = String(sortOrder).split('-')

  const found =
    1 < parts.length
      ? cols.findIndex(col => col.param === parts[0])
      : cols.findIndex(col => (('i' === sortOrder[0] && 'title') || 'progress') === col.type)

  const index = 0 > found ? cols.length - 1 : found
  const direction = 2 === parts.length ? ('asc' === parts[1] && 1) || -1 : ('asc' === sortOrder.slice(1) && 1) || -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

// A polarity scale reads each sub-question as the score it landed on. The points a respondent
// assigns are a magnitude on the question's own scale, not a share of anything, so they carry the
// scale's own unit rather than the percent sign its authoring template happens to be written with.
const calcScaleTable = ({ answer, batches, type, scalePow }) => {
  const isLoading = 'loading' === answer

  return {
    cols: [
      { type: 'title', title: TITLE, sort: byPosition },
      {
        type: 'value',
        title: basedOnTitle(answer),
        width: 190,
        sort: byCellValue(1, 'value'),
        format: value => `${formatAmounts(value, null, scalePow || 2)} score`
      }
    ],
    rows: batches.map(batch => ({
      position: batch.position,
      cells: [
        titleCell({ batch, type }),
        { value: batchAnswerOf(answer, batch.position).scale?.amount || 0, loading: isLoading }
      ]
    }))
  }
}

// A scale or ranking question reads each sub-question as the average score it was given.
const calcScoreTable = ({ answer, batches, type }) => ({
  cols: [
    { type: 'title', title: TITLE, sort: byPosition },
    {
      type: 'value',
      title: basedOnTitle(answer),
      width: 190,
      sort: byCellValue(1, 'value'),
      format: value => `${formatAmounts(value, null, 2)} score`
    }
  ],
  rows: batches.map(batch => {
    const batchAnswer = batchAnswerOf(answer, batch.position)
    const value = Object.hasOwn(batchAnswer, 'scale')
      ? batchAnswer.scale.amount || 0
      : batchAnswer.score / batchAnswer.total || 0

    return { position: batch.position, cells: [titleCell({ batch, type }), { value, ...loadingMark(answer) }] }
  })
})

// A matrix read as means reads each sub-question as the arithmetic average of the answers it drew.
const calcMeanTable = ({ answer, batches, type }) => {
  const sqExist = calcSqExist(answer, batches)

  const cols = [
    { type: 'title', title: TITLE, sort: byPosition },
    { type: 'value', title: 'Mean', sort: byCellValue(sqExist ? 2 : 1, 'value'), format: value => formatAmounts(value) }
  ]

  const rows = batches.map(batch => {
    const batchAnswer = batchAnswerOf(answer, batch.position)
    const cells = [titleCell({ batch, type }), { value: batchAnswer.mean?.value || 0, ...loadingMark(answer) }]

    if (sqExist) cells.splice(1, 0, { value: batchAnswer.total || 0, ...loadingMark(answer) })

    return { position: batch.position, cells }
  })

  if (sqExist) cols.splice(1, 0, SQ_COLUMN)

  return { cols, rows }
}

// A matrix read as box scores reads each sub-question as the share of respondents who answered
// within the box the strip above is set to.
const calcBoxTable = ({ answer, batches, type, topsMode, maxAnswerPosition, percentDecimals }) => {
  const sqExist = calcSqExist(answer, batches)
  const tops = getChartTopsMapping(type, batches, maxAnswerPosition).find(item => item.value === topsMode)

  const cols = [
    { type: 'title', title: TITLE, sort: byPosition },
    {
      type: 'progress',
      title: `${tops?.text || ''} %`,
      percentDecimals,
      sort: byCellValue(sqExist ? 2 : 1, 'percents')
    }
  ]

  const rows = batches.map(batch => {
    const box = batchAnswerOf(answer, batch.position).box?.[topsMode]
    const cells = [
      titleCell({ batch, type }),
      { amount: box?.amount || 0, percents: box?.percents || 0, ...loadingMark(answer) }
    ]

    if (sqExist) cells.splice(1, 0, { value: batchAnswerOf(answer, batch.position).total || 0, ...loadingMark(answer) })

    return { position: batch.position, cells }
  })

  if (sqExist) cols.splice(1, 0, SQ_COLUMN)

  return { cols, rows }
}

// Every list is built the same way once its columns and rows are known: the stored sort names one
// of the columns, and the question's own decimals win over the report's.
const buildTableProps =
  build =>
  ({ id, format, answer, batches, sortOrder = 'iasc', handlers = {}, ...question } = {}) => {
    const percentDecimals = Object.hasOwn(format || {}, 'percentDecimals')
      ? format.percentDecimals
      : getModelValue('percentDecimals')

    const table = build({
      ...question,
      answer: answer || {},
      batches: (batches || []).filter(batch => batch),
      percentDecimals
    })

    return { id, cols: applySortedColumn(table.cols, sortOrder), rows: table.rows, onSort: handlers.onSort }
  }

// A polarity scale reads its sub-questions as points on its own scale.
export const calcScaleTableProps = buildTableProps(calcScaleTable)

// A scale or ranking question reads them as the average score they were given.
export const calcScoreTableProps = buildTableProps(calcScoreTable)

// A matrix read as means reads them as the arithmetic average of the answers they drew.
export const calcMeanTableProps = buildTableProps(calcMeanTable)

// A matrix read as box scores reads them as the share inside the box the strip is set to.
export const calcBoxTableProps = buildTableProps(calcBoxTable)
